<template>
  <div>
    <div class="mb-1">
      <div class="note-title">
        <div class="row">
          <div class="d-flex align-items-center col-md-4 col-lg-3">
            <span>{{ note.label }} </span>
          </div>
          <div class="col-md-8 col-lg-9" ref="dropdownWrapper">
            <div 
              v-if="!showDropdown && noteNameList.length > 0"
              class="selected-notes-display d-flex justify-content-between align-items-center mb-1"
              @click="handleTextClick"
            >
              <div class="d-flex flex-wrap align-items-center gap-1 w-100">
                <div 
                  v-for="(n, idx) in noteNameList" 
                  :key="idx"
                  class="selected-note-badge badge bg-light text-dark border d-flex align-items-center"
                  @click.stop
                  @dblclick.stop="handleTextDoubleClick(idx, n)"
                >
                <span v-if="editingIndex !== idx">{{ n.name }}</span>
                <textarea 
                  v-else
                  v-model="editingValue"
                  class="selected-note-input form-control form-control-sm"
                  rows="1"
                  @keydown.enter.prevent.stop="saveEdit(idx)"
                  @blur="cancelEdit"
                  @click.stop
                  @dblclick.stop
                  ref="editInputRefs"
                  @input="(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }"
                ></textarea>
              </div>
              </div>
              <div class="ps-2 pe-1 text-muted" style="cursor: pointer;">
                <i class="fas fa-chevron-down"></i>
              </div>
            </div>
            
            <div v-show="showDropdown || noteNameList.length === 0">
              <AsyncSelect
                ref="asyncSelectRef"
                placeholder="Select Notes"
                v-model="noteNameList"
                @update:modelValue="showDropdown = false"
                :api-service="noteNameByType"
                :additional-query="{ type: note.value }"
                :label="'name'"
                :reduce="item => item"
                :is-new-added="true"
                :is-multiple="true"
                @newAdd="handleNewAdded"
              />
            </div>
          </div>
        </div>
        
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, ref, onMounted, onUnmounted, nextTick } from "vue";
import { useRoute } from "vue-router";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleHospital from '@/services/modules/hospital';

const { noteNameByType, storeNote } = handleHospital();
const route = useRoute();

const props = defineProps({
  note: Object,
  index: Number,
});

const internalNoteNames = ref([]);

watch(
  () => props.note.note_names,
  (newVal) => {
    internalNoteNames.value = newVal ? newVal.map(name => ({ name })) : [];
  },
  { immediate: true }
);

const noteNameList = computed({
  get() {
    return internalNoteNames.value;
  },
  set(newVal) {
    internalNoteNames.value = newVal;
  }
});

const showDropdown = ref(false);
const dropdownWrapper = ref(null);

const handleClickOutside = (event) => {
  if (dropdownWrapper.value && !dropdownWrapper.value.contains(event.target) && editingIndex.value === -1) {
    showDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});

const editingIndex = ref(-1);
const editingValue = ref('');

const asyncSelectRef = ref(null);

const handleTextClick = () => {
  showDropdown.value = true;
  nextTick(() => {
    if (!asyncSelectRef.value) return;
    
    const vSelect = asyncSelectRef.value.dropdownRef;
    if (!vSelect) return;

    if (vSelect.searchEl) {
      vSelect.searchEl.focus();
      return;
    }
    
    if (!vSelect.$el) return;
    
    const input = vSelect.$el.querySelector('input[type="search"]');
    if (!input) return;
    
    input.focus();
  });
};

const handleTextDoubleClick = (idx, note) => {
  showDropdown.value = false;
  editingIndex.value = idx;
  editingValue.value = note.name;
  nextTick(() => {
    const inputs = document.querySelectorAll('.selected-notes-display textarea');
    if (!inputs.length) return;
    const textarea = inputs[0];
    textarea.focus();
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  });
};

const saveEdit = async (idx) => {
  const newValue = editingValue.value.trim();
  const oldValue = noteNameList.value[idx].name;

  if (newValue === '' || newValue === oldValue) {
    editingIndex.value = -1;
    editingValue.value = '';
    return;
  }

  const newNote = { name: newValue };
  await handleNewAdded(newNote);
  
  const newList = [...noteNameList.value];
  newList[idx] = newNote;
  noteNameList.value = newList;
  
  editingIndex.value = -1;
  editingValue.value = '';
};

const cancelEdit = () => {
  editingIndex.value = -1;
  editingValue.value = '';
};

const handleNewAdded = async (newOption) => {
  const res = await storeNote({
    company_id: route.params.companyId,
    type: props.note.value,
    name: newOption.name,
    status: 'active',
  });
  if(!res.status) {
    console.log('Something went wrong');
    return;
  }
};

defineExpose({
  getNoteData: () => ({
    id: props.note.id || null,
    type: props.note.value,
    new_notes: [],
    note: JSON.stringify(noteNameList.value.map(n => n.name))
  })
});
</script>

<style>
.note-head {
    display: flex;
    margin-top: 2%;
    justify-content: space-between;
    margin-bottom: 1%;
}
.note-body .card {
    background: #f9f9f9;
    min-height: 100px;
    margin-bottom: 1rem;
}

.note-body .card-body {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
}

.note-section {
    margin-top: 2%;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    width: 96%;
    margin-left: 2%;
}

.selected-notes-display {
    min-height: 38px;
    border: 1px solid #d8d6de;
    border-radius: 0.357rem;
    padding: 0.4rem;
    cursor: pointer;
    gap: 0.25rem;
}

.selected-note-badge {
    padding: 0.4rem 0.5rem;
    font-size: 0.9rem;
    max-width: 100%;
    white-space: normal;
    text-align: left;
    word-break: break-word;
}

.selected-note-input {
    min-width: 250px;
    width: 100%;
    padding: 4px 6px;
    display: block;
    resize: none;
    overflow: hidden;
}

.v-select .vs__selected {
  max-width: 100%;
  word-break: break-word;
  white-space: normal;
}
.v-select .vs__dropdown-option {
  word-break: break-word;
  white-space: normal;
}
.v-select .vs__dropdown-menu {
  overflow-x: hidden;
}
</style>